import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useBastionCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { BastionTier, BastionRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const TIER_INFO: Record<BastionTier, { label: string; description: string; features: string[] }> = {
  basic: {
    label: 'Basic',
    description: 'RDP/SSH over browser. Fixed 2 scaling units, no custom ports.',
    features: ['Browser RDP/SSH', 'Fixed 2 scaling units', 'No custom ports', 'No private-only deployment'],
  },
  standard: {
    label: 'Standard',
    description: 'Adds shareable links, IP-based connection, custom ports, and native client support.',
    features: ['All Basic features', 'Shareable links', 'IP-based connection', 'Custom ports', 'Native client (RDP/SSH)', 'Azure AD auth'],
  },
  premium: {
    label: 'Premium',
    description: 'Adds session recording, private-only deployment, and enhanced security posture.',
    features: ['All Standard features', 'Session recording', 'Private-only deployment', 'Microsoft Defender integration'],
  },
}

export default function BastionCalc() {
  const [tier, setTier] = useState<BastionTier>('standard')
  const [scalingUnits, setScalingUnits] = useState(2)
  const [outboundGb, setOutboundGb] = useState(0)

  const inputs: BastionRequest = { tier, scalingUnits, monthlyOutboundGb: outboundGb }
  const { data: result, isLoading, isError } = useBastionCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (result?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'bastion', result_gbp: result.monthly_estimate, tier })
    }
  }, [result?.monthly_estimate])

  const isBasic = tier === 'basic'
  const info = TIER_INFO[tier]

  return (
    <div className="space-y-6">
      <TrustBar />

      {/* Always-on billing trap — headline warning */}
      <div className="p-4 rounded-lg flex gap-3" style={{ backgroundColor: 'rgba(164,38,44,0.08)', border: '1px solid rgba(164,38,44,0.4)' }}>
        <span className="material-symbols-outlined text-[#a4262c] text-[20px] shrink-0 mt-0.5">warning</span>
        <div>
          <p className="text-[13px] font-semibold mb-0.5" style={{ color: '#a4262c' }}>Azure Bastion bills 24/7 — even when idle</p>
          <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
            Unlike VMs you can deallocate, Bastion runs continuously once deployed. Standard tier costs ~£159/month whether anyone connects or not. Only deploy Bastion if it is permanently needed.
          </p>
        </div>
      </div>

      {/* Tier + config inputs */}
      <div className="p-5 rounded-lg space-y-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Bastion configuration</h2>

        {/* Tier selector */}
        <div>
          <label className="flex items-center text-[13px] font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Tier
            <FieldTooltip content="Basic is fixed-cost but lacks native client, custom ports, and IP-based connections. Standard adds those at ~53% more. Premium adds session recording and private-only deployment for regulated environments." />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(['basic', 'standard', 'premium'] as BastionTier[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTier(t)
                  if (t === 'basic') setScalingUnits(2)
                }}
                className="p-3 rounded text-left transition-colors"
                style={{
                  border: tier === t ? '2px solid #0078d4' : '1px solid var(--outline-variant)',
                  backgroundColor: tier === t ? 'rgba(0,120,212,0.06)' : 'var(--bg-surface)',
                }}
              >
                <p className="text-[13px] font-semibold mb-0.5" style={{ color: tier === t ? '#0078d4' : 'var(--text-primary)' }}>{TIER_INFO[t].label}</p>
                <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{TIER_INFO[t].description}</p>
              </button>
            ))}
          </div>
          {/* Feature chips */}
          <div className="flex flex-wrap gap-1 mt-2">
            {info.features.map(f => (
              <span key={f} className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,120,212,0.08)', color: '#0078d4', border: '1px solid rgba(0,120,212,0.2)' }}>{f}</span>
            ))}
          </div>
        </div>

        {/* Scaling units */}
        <div>
          <label className="flex items-center text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Scaling units
            <FieldTooltip content="Each scaling unit supports ~25 concurrent RDP/SSH sessions. Basic is fixed at 2 units. Standard and Premium can scale 2–50 units. Each additional unit adds ~£0.1053/hr to the base cost." />
          </label>
          {isBasic ? (
            <p className="text-[12px] px-3 py-2 rounded" style={{ color: 'var(--text-tertiary)', backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              Fixed at 2 units (Basic tier)
            </p>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <input
                  type="range" min={2} max={50} step={1} value={scalingUnits}
                  onChange={(e) => setScalingUnits(Number(e.target.value))}
                  className="flex-1 accent-[#0078d4]"
                />
                <span className="text-[13px] font-mono w-8 text-right" style={{ color: 'var(--text-primary)' }}>{scalingUnits}</span>
              </div>
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Supports ~{scalingUnits * 25} concurrent sessions · {scalingUnits} units × £0.1053/hr
              </p>
            </div>
          )}
        </div>

        {/* Outbound data */}
        <div>
          <label className="flex items-center text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Monthly outbound data (GB)
            <FieldTooltip content="Inbound traffic (connecting to VMs) is free. Only outbound data from Azure over Bastion is billed at standard egress rates. For typical RDP/SSH sessions this is usually negligible." />
          </label>
          <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>Inbound is free. First 100 GB outbound free.</p>
          <input
            type="number" value={outboundGb} min={0} max={100000} step={10}
            onChange={(e) => setOutboundGb(Math.max(0, Number(e.target.value)))}
            className="w-full rounded px-3 py-2 text-[13px] border"
            style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
          />
        </div>
      </div>

      {/* Result panel */}
      {isLoading && (
        <div className="p-5 rounded-lg text-center text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Calculating…
        </div>
      )}
      {isError && (
        <div className="p-5 rounded-lg text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: '#a4262c' }}>
          Could not fetch pricing. Check your inputs and try again.
        </div>
      )}
      {result && !isLoading && (
        <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Estimated monthly cost</h2>
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>≈ £{fmt(result.per_day)}/day</p>
            </div>
            <span className="text-[28px] md:text-[36px] font-bold" style={{ color: '#0078d4' }}>£{fmt(result.monthly_estimate)}</span>
          </div>

          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
            {result.breakdown?.map((item: { label: string; value: number; unit?: string; isFree?: boolean }) => (
              <div key={item.label} className="flex justify-between text-[13px] gap-2">
                <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                <span style={{ color: item.isFree ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                  {item.isFree ? 'Free' : `£${fmt(item.value)}`}
                </span>
              </div>
            ))}
          </div>

          {/* Tier comparison */}
          {result.tier_comparison && result.tier_comparison.length > 0 && (
            <div className="p-3 rounded text-[13px] space-y-2" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Tier comparison (same scaling units)</p>
              {result.tier_comparison.map((row: { tier: string; monthly: number; isSelected: boolean }) => (
                <div key={row.tier} className="flex justify-between items-center">
                  <span style={{ color: row.isSelected ? '#0078d4' : 'var(--text-secondary)', fontWeight: row.isSelected ? 600 : 400 }}>
                    {TIER_INFO[row.tier as BastionTier]?.label ?? row.tier}
                    {row.isSelected && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,120,212,0.1)', color: '#0078d4' }}>selected</span>}
                  </span>
                  <span style={{ color: row.isSelected ? '#0078d4' : 'var(--text-primary)' }}>£{fmt(row.monthly)}/mo</span>
                </div>
              ))}
            </div>
          )}

          {result.warnings?.map((w: string) => (
            <div key={w} className="flex gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">info</span>
              <span>{w}</span>
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <ShareButton inputs={inputs} result={result} service="bastion" />
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/azure-bastion/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-1" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>

          <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{result.disclaimer}</p>
        </div>
      )}

      {/* Billing traps */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[16px] align-middle mr-1">warning</span>
          Azure Bastion billing traps
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'No off switch',
              body: 'Bastion has no deallocate option. The only way to stop billing is to delete the resource. Build deletion into your offboarding runbook.',
            },
            {
              title: 'Scaling units are cumulative',
              body: 'Standard starts at 2 units (included in the base rate). Each additional unit adds ~£0.10/hr on top. At 10 units you pay ~£0.84/hr rather than ~£0.22/hr.',
            },
            {
              title: 'Per-host vs shared deployment',
              body: 'One Bastion per VNet. If you have 3 VNets with peering you still typically need 3 Bastion instances — tripling the cost. Consider hub-spoke consolidation.',
            },
            {
              title: 'VPN Gateway is cheaper for bulk access',
              body: 'If many users need persistent network-level access, a VPN Gateway (from ~£104/month for VpnGw1) with P2S VPN may be cheaper than Standard Bastion at scale.',
            },
          ].map(trap => (
            <div key={trap.title} className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{trap.title}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{trap.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cross-links */}
      <div className="space-y-2">
        <Link
          to="/vpn-gateway"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>vpn_key</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>VPN Gateway calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Network-level access for many users — often cheaper than Bastion at scale</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
        <Link
          to="/networking"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>hub</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Networking calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Public IPs, NAT Gateway, DDoS Protection, and NSG flow logs</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
